import {
  hasPermission,
  LICENSE_MODE_LABELS,
  LICENSE_STATUS_LABELS,
  SEAT_STATUS_LABELS,
} from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Tags } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  createResource,
  type LicenseRow,
  listResource,
  type MaterialRow,
  useReferenceData,
} from "@/lib/master-data";
import { formatDate } from "@/lib/utils";

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "danger" | "secondary"> = {
  active: "success",
  expiring: "warning",
  expired: "danger",
  terminated: "secondary",
  renewed: "secondary",
  available: "success",
  pending: "warning",
  allocated: "default",
  reclaimed: "secondary",
  revoked: "danger",
};

export function LicensesPage() {
  const { data: me } = useMe();
  const reference = useReferenceData();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [renewLicense, setRenewLicense] = useState<LicenseRow | null>(null);
  const [renewDate, setRenewDate] = useState("");
  const [renewPrice, setRenewPrice] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [selected, setSelected] = useState<LicenseRow | null>(null);
  const [allocateSeatId, setAllocateSeatId] = useState<string | null>(null);
  const [allocateUserId, setAllocateUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    materialId: "",
    mode: "subscription",
    startDate: new Date().toISOString().slice(0, 10),
    expiryDate: `${new Date().getFullYear() + 1}-09-30`,
    price: "",
    totalSeats: "5",
    vendor: "",
    softwareVersion: "",
    billingCycleMonths: "12",
  });

  const canManage = Boolean(me && hasPermission(me.permissions, "license:manage"));
  const canManageSeats = Boolean(me && hasPermission(me.permissions, "license:seat:manage"));
  const canRenew = Boolean(me && hasPermission(me.permissions, "license:renew"));
  const licensesQuery = useQuery({
    queryKey: ["licenses", page],
    queryFn: () =>
      apiFetch<{ items: LicenseRow[]; total: number }>(
        `/api/v1/licenses?page=${page}&pageSize=${PAGE_SIZE}`,
      ),
  });
  const materialsQuery = useQuery({
    queryKey: ["materials"],
    queryFn: () => listResource<MaterialRow>("/api/v1/materials"),
  });
  const licenses = licensesQuery.data?.items ?? [];
  const totalLicenses = licensesQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalLicenses / PAGE_SIZE));
  const effectivePage = Math.min(page, totalPages);
  const pageRows = licenses;
  const materials = materialsQuery.data ?? [];
  const softwareMaterials = materials.filter(
    (item) => item.category === "software" && item.status === "active",
  );
  const materialById = useMemo(
    () => new Map(materials.map((item) => [item.id, item])),
    [materials],
  );
  const users = reference.data?.users ?? [];
  const selectedLive = selected
    ? (licenses.find((item) => item.id === selected.id) ?? selected)
    : null;

  const stats = {
    totalSeats: licenses.reduce((sum, item) => sum + item.totalSeats, 0),
    allocated: licenses.reduce((sum, item) => sum + item.allocatedSeats, 0),
    expiring: licenses.filter((item) => ["active", "expiring"].includes(item.status)).length,
  };

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["licenses"] });
  }

  async function createLicense() {
    if (!form.materialId) return toast.error("请选择软件物料");
    if (!form.price.trim() || !form.totalSeats.trim()) return toast.error("请填写价格与席位数");
    if (form.mode === "subscription" && !form.expiryDate)
      return toast.error("订阅制必须填写到期日");
    setBusy(true);
    try {
      await createResource("/api/v1/licenses", {
        materialId: form.materialId,
        mode: form.mode,
        startDate: form.startDate,
        expiryDate: form.mode === "perpetual" ? null : form.expiryDate || null,
        purchaseUnitPriceYuan: Number(form.price),
        totalSeats: Number(form.totalSeats),
        vendor: form.vendor || null,
        softwareVersion: form.softwareVersion || null,
        billingCycleMonths: Number(form.billingCycleMonths),
      });
      toast.success("软件许可已登记");
      setCreateOpen(false);
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "登记失败");
    } finally {
      setBusy(false);
    }
  }

  async function allocateSeat() {
    if (!selectedLive || !allocateSeatId) return;
    if (!allocateUserId) return toast.error("请选择使用人");
    setBusy(true);
    try {
      await createResource(`/api/v1/licenses/${selectedLive.id}/seats/allocate`, {
        userId: allocateUserId,
        confirmationText: "管理员分配席位",
      });
      toast.success("席位已分配，等待确认");
      setAllocateSeatId(null);
      setAllocateUserId("");
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "分配失败");
    } finally {
      setBusy(false);
    }
  }

  async function seatAction(
    seatId: string,
    action: "confirm" | "cancel" | "reclaim" | "revoke" | "reallocate",
  ) {
    setBusy(true);
    try {
      await createResource(`/api/v1/license-seats/${seatId}/${action}`, {});
      toast.success(
        action === "confirm"
          ? "席位已确认"
          : action === "cancel"
            ? "待确认席位已取消"
            : action === "reclaim"
              ? "席位已回收"
              : action === "revoke"
                ? "席位已吊销"
                : "席位已恢复可用",
      );
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "操作失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="软件许可"
        description="许可、席位与使用人分离管理；订阅制到期提醒、回收与吊销均保留审计。"
        actions={
          <>
            <Button variant="outline" onClick={() => licensesQuery.refetch()}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            {canManage ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                登记许可
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="总席位" value={stats.totalSeats} icon={Tags} />
        <StatCard label="已分配" value={stats.allocated} icon={Tags} accent="warning" />
        <StatCard label="有效许可" value={stats.expiring} icon={Tags} accent="success" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>许可列表</CardTitle>
          <CardDescription>点击“席位”查看分配明细；待确认席位需管理员确认后生效。</CardDescription>
        </CardHeader>
        <CardContent>
          {licensesQuery.isLoading ? (
            <div className="space-y-2">
              {["s1", "s2", "s3"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : totalLicenses === 0 ? (
            <EmptyState title="暂无软件许可" description="登记许可后自动生成席位。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>许可编号</TableHead>
                  <TableHead>软件</TableHead>
                  <TableHead>模式</TableHead>
                  <TableHead>席位</TableHead>
                  <TableHead>到期日</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((license) => (
                  <TableRow key={license.id}>
                    <TableCell className="font-mono text-xs">{license.licenseCode}</TableCell>
                    <TableCell>{materialById.get(license.materialId)?.name ?? "—"}</TableCell>
                    <TableCell>
                      {LICENSE_MODE_LABELS[license.mode as keyof typeof LICENSE_MODE_LABELS] ??
                        license.mode}
                    </TableCell>
                    <TableCell>
                      {license.allocatedSeats} / {license.totalSeats}
                    </TableCell>
                    <TableCell>{formatDate(license.expiryDate)}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[license.status] ?? "secondary"}>
                        {LICENSE_STATUS_LABELS[
                          license.status as keyof typeof LICENSE_STATUS_LABELS
                        ] ?? license.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelected(license);
                          setAllocateSeatId(null);
                        }}
                      >
                        席位
                      </Button>
                      {canRenew && license.mode !== "perpetual" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRenewLicense(license);
                            setRenewDate(license.expiryDate ?? "");
                            setRenewPrice(
                              license.purchaseUnitPriceCents
                                ? String(license.purchaseUnitPriceCents / 100)
                                : "",
                            );
                          }}
                        >
                          续期
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!licensesQuery.isLoading && totalLicenses > 0 ? (
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                共 {totalLicenses} 条 · 第 {effectivePage} / {totalPages} 页
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((value) => value + 1)}
                >
                  下一页
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={Boolean(renewLicense)} onOpenChange={(open) => !open && setRenewLicense(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>许可续期：{renewLicense?.licenseCode}</DialogTitle>
            <DialogDescription>续期后状态恢复为有效；历史到期日保留在审计日志。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>新到期日</Label>
              <Input
                type="date"
                value={renewDate}
                onChange={(event) => setRenewDate(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>续费单价（元，可选）</Label>
              <Input
                type="number"
                value={renewPrice}
                onChange={(event) => setRenewPrice(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewLicense(null)}>
              取消
            </Button>
            <Button
              disabled={!renewDate}
              onClick={async () => {
                if (!renewLicense) return;
                try {
                  await apiFetch(`/api/v1/licenses/${renewLicense.id}/renew`, {
                    method: "POST",
                    body: JSON.stringify({
                      expiryDate: renewDate,
                      ...(renewPrice ? { purchaseUnitPriceYuan: Number(renewPrice) } : {}),
                    }),
                  });
                  await queryClient.invalidateQueries({ queryKey: ["licenses"] });
                  await queryClient.invalidateQueries({ queryKey: ["reminders"] });
                  toast.success("许可已续期");
                  setRenewLicense(null);
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : "续期失败");
                }
              }}
            >
              确认续期
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>登记软件许可</DialogTitle>
            <DialogDescription>按物料自动生成许可编号与全部席位。</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>软件物料</Label>
              <select
                value={form.materialId}
                onChange={(event) => setForm({ ...form, materialId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择软件物料</option>
                {softwareMaterials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.code} · {material.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>授权模式</Label>
              <select
                value={form.mode}
                onChange={(event) => setForm({ ...form, mode: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="subscription">订阅制</option>
                <option value="term">期限制</option>
                <option value="perpetual">永久买断</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>总席位</Label>
              <Input
                type="number"
                min="1"
                value={form.totalSeats}
                onChange={(event) => setForm({ ...form, totalSeats: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>开始日期</Label>
              <Input
                type="date"
                value={form.startDate}
                onChange={(event) => setForm({ ...form, startDate: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>到期日期</Label>
              <Input
                type="date"
                value={form.expiryDate}
                disabled={form.mode === "perpetual"}
                onChange={(event) => setForm({ ...form, expiryDate: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>单价（元）</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(event) => setForm({ ...form, price: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>计费周期（月）</Label>
              <Input
                type="number"
                min="1"
                value={form.billingCycleMonths}
                onChange={(event) => setForm({ ...form, billingCycleMonths: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>供应商</Label>
              <Input
                value={form.vendor}
                onChange={(event) => setForm({ ...form, vendor: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>软件版本</Label>
              <Input
                value={form.softwareVersion}
                onChange={(event) => setForm({ ...form, softwareVersion: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button onClick={createLicense} disabled={busy}>
              确认登记
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>席位：{selectedLive?.licenseCode}</DialogTitle>
            <DialogDescription>
              可用 → 待确认 → 已分配；已分配可回收/吊销，已回收可重新分配。
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-auto">
            {selectedLive ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>席位</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>使用人</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedLive.seats.map((seat) => (
                    <TableRow key={seat.id}>
                      <TableCell className="font-mono text-xs">{seat.seatCode}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[seat.status] ?? "secondary"}>
                          {SEAT_STATUS_LABELS[seat.status as keyof typeof SEAT_STATUS_LABELS] ??
                            seat.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {users.find((user) => user.id === seat.userId)?.displayName ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canManageSeats && ["available", "reclaimed"].includes(seat.status) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setAllocateSeatId(seat.id);
                                setAllocateUserId("");
                              }}
                            >
                              分配
                            </Button>
                          ) : null}
                          {canManageSeats && seat.status === "reclaimed" ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => seatAction(seat.id, "reallocate")}
                            >
                              恢复
                            </Button>
                          ) : null}
                          {canManageSeats && seat.status === "pending" ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => seatAction(seat.id, "confirm")}
                              >
                                确认
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => seatAction(seat.id, "cancel")}
                              >
                                取消
                              </Button>
                            </>
                          ) : null}
                          {canManageSeats && seat.status === "allocated" ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => seatAction(seat.id, "reclaim")}
                              >
                                回收
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => seatAction(seat.id, "revoke")}
                              >
                                吊销
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </div>
          {allocateSeatId ? (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <p className="text-sm font-medium">分配席位</p>
              <select
                value={allocateUserId}
                onChange={(event) => setAllocateUserId(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择使用人</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAllocateSeatId(null)}>
                  取消
                </Button>
                <Button onClick={allocateSeat} disabled={busy}>
                  确认分配
                </Button>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
